import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

/**
 * Shape `PaymentHistoryApiMapper` formats its dates into. Matched explicitly rather than
 * handed to `dayjs()`, which reads a bare `DD/MM/YYYY` through its fallback parser and would
 * take `03/08/2026` as March 8th.
 */
const PAYMENT_DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * Whether a payment was charged within the calendar month currently running — that is, whether
 * the window that closes on the last day of its month is still open.
 *
 * The year is part of the comparison: the same month one year back is not the current month.
 *
 * Callers pass the payment's `createdAt`, not its `paymentDate`: the latter names the period
 * being charged and runs ahead of the charge, so it would place a payment collected in August
 * in September and close this window before it opened.
 *
 * A date that is missing or does not match the expected format returns `false` — when the period
 * cannot be established, the safe answer is to withhold the month-scoped affordance rather than
 * to offer it.
 *
 * @param chargedAt - Charge date as the view DTO carries it (`DD/MM/YYYY`, or '' when unknown)
 * @param now - Reference point, injectable so tests do not depend on the wall clock
 * @returns `true` when the payment falls in the current month and year
 *
 * @example
 * isPaymentFromCurrentMonth('07/08/2026', dayjs('2026-08-20')); // true — until 31/08 included
 * isPaymentFromCurrentMonth('07/07/2026', dayjs('2026-08-20')); // false
 */
export const isPaymentFromCurrentMonth = (chargedAt: string | null | undefined, now: Dayjs = dayjs()): boolean => {
  const match = chargedAt?.trim().match(PAYMENT_DATE_PATTERN);

  if (!match) return false;

  const [, , month, year] = match;

  return Number(month) === now.month() + 1 && Number(year) === now.year();
};
